import { NavigationDrawer, INavigationDrawer } from '@sanlam-glacier/app-common/components';
import { SecurityAccessFeaturesEnum } from 'bff';
import { UIMatch, useMatches, useNavigate } from 'react-router-dom';
import { generateProposalOverviewPath, routes } from 'routes/route-paths';
import './SideNavMenu.scss';
import { useEffect } from 'react';
import { useFeature } from 'modules/app/components/atoms/feature-flags/useFeature';
import { useConfig } from 'providers/config/ConfigProvider';
import { useUserContext } from 'providers/managers/user-context-manager/user-context-manager';

type Handle = {
  topLevelNavigationItem?: boolean;
};

interface SidNavMenuProps {
  mountSideNav: () => void;
}

type IData = Required<INavigationDrawer>['data'][number];
type IDataMenuItem = IData['items'][number];

export const SideNavMenu = ({ mountSideNav }: SidNavMenuProps) => {
  useEffect(() => {
    mountSideNav();
  }, []);
  const matches = useMatches() as UIMatch<unknown, Handle>[];
  const topLevelMatch = matches.find((match) => !!match.handle?.topLevelNavigationItem);
  const matchingRoutePath = topLevelMatch ? topLevelMatch.pathname : routes.proposals;
  const userContext = useUserContext();
  const shouldShowOtherApps = useFeature('showOtherApps');
  const shouldShowDashboard = useFeature('showDashboard');
  const shouldShowClient = useFeature('showClient');
  const shouldShowReports = useFeature('showReports');
  const shouldShowCaseTracking = useFeature('showCaseTracking');
  const shouldShowMyPractice = useFeature('showMyPractice');
  const navigate = useNavigate();
  const { routerBasePath } = useConfig();
  const isNewBusinessAllowed = userContext.canAccessAll([
    SecurityAccessFeaturesEnum.ProposalOverview
  ]);
  const navigationItems = [
    {
      id: 'primary-navigation',
      title: 'Primary links',
      items: []
    }
  ] as IData[];

  if (shouldShowDashboard) {
    navigationItems[0]?.items.push({
      id: routes.dashboard,
      title: 'Dashboard',
      icon: 'action-2_dashboard'
    } as IDataMenuItem);
  }

  if (shouldShowClient) {
    navigationItems[0]?.items.push({
      id: routes.clients,
      title: 'Clients',
      icon: 'social-media_people'
    } as IDataMenuItem);
  }

  if (isNewBusinessAllowed) {
    navigationItems[0]?.items.push({
      id: generateProposalOverviewPath(),
      title: 'New business',
      icon: 'action_note-add'
    } as IDataMenuItem);
  }

  if (shouldShowCaseTracking) {
    navigationItems[0]?.items.push({
      id: routes.caseTracking,
      title: 'Tracking',
      icon: 'action_track'
    } as IDataMenuItem);
  }

  if (shouldShowMyPractice) {
    navigationItems[0]?.items.push({
      id: routes.myPractice,
      title: 'My practice',
      icon: 'places_business-center'
    } as IDataMenuItem);
  }

  navigationItems.push({
    id: 'secondary-navigation',
    title: 'Secondary links',
    items: []
  } as IData);

  if (shouldShowOtherApps) {
    navigationItems[1]?.items.push({
      id: routes.otherApps,
      title: 'Other apps',
      icon: 'navigation_apps'
    } as IDataMenuItem);
  }

  navigationItems[1]?.items.push({
    id: routes.legal,
    title: 'Legal',
    icon: 'action-2_secured-file'
  } as IDataMenuItem);

  navigationItems[1]?.items.push({
    id: routes.support,
    title: 'Support',
    icon: 'action-2_contact-support'
  } as IDataMenuItem);

  if (shouldShowReports) {
    navigationItems[1]?.items.push({
      id: routes.reports,
      title: 'Reports',
      icon: 'av_library-books'
    } as IDataMenuItem);
  }

  const activeItem = navigationItems
    .flatMap((x) => x.items)
    .find((item) => matchingRoutePath.includes(item.id)) ?? {
    id: generateProposalOverviewPath(),
    title: 'Proposals',
    items: []
  };

  const handleNavigate = (clickedItem: IData) => {
    if (clickedItem.id === routes.legal) {
      const path = `${routerBasePath}/${clickedItem.id}`;
      // remove extra forward slashes to correctly route legal page
      const pathCleanedUp = path.replaceAll(/\/{2,}/g, '/');
      // new tab links, cannot infer routerBasePath here
      window.open(pathCleanedUp, '_blank');
    } else {
      navigate(clickedItem.id);
    }
  };

  return (
    <NavigationDrawer
      className='navigation-drawer'
      data={navigationItems}
      onClick={handleNavigate}
      activeItem={activeItem as IData}
    />
  );
};
